import {Component,OnDestroy,OnInit,ViewChild} from "@angular/core";
import {NgForm} from "@angular/forms";
import {TypeCharge} from "../../../../../../domain/prestation/type-charge";
import {TypesChargesService} from "@services/admin/facture/types-charges.service";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {ConfirmService} from "../../../../../../share/component/confirmation/confirm.service";
import {ActivatedRoute,Router} from "@angular/router";
import {Result,TypeCodeErreur} from "../../../../../../domain/common/http/result";
import {filter} from "rxjs/operators";
import {FloatingButtonAction,TypeAction} from "../../../../../../share/component/floating-button/floating-button";
import {BehaviorSubject} from "rxjs";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {FacturesTypesChargesInfosIconesComponent} from "./factures-types-charges-infos-icones.component";
import {NatureVoyage} from "@domain/prestation/nature-voyage";
import {FraisFacturesService} from "@components/admin/frais/factures/frais-factures.service";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {FraisFacturesComponent,Onglets} from "../../frais-factures.component";

/**
 * Composant d'affichage des informations du cadre "Types de charges"
 */
@Component({
    host: {'data-test-id': 'factures-types-charge-infos'},
    selector: 'factures-types-charge-infos',
    templateUrl: './factures-types-charges-infos.component.html'
})
export class FacturesTypesChargesInfosComponent implements OnInit, OnDestroy {
    /** Type de charge sélectionnée */
    typeCharge: TypeCharge;

    /** Booléen pour savoir si l'affichage du formulaire se fait en création ou en modification, pour le champ du code (lecture seule si on est en modification) */
    isCreation: boolean;

    /** Formulaire */
    @ViewChild('form') form: NgForm;

    /** Liste des actions possibles pour le floatingButton en bas à droite */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Liste des différents onglets disponibles dans le menu frais */
    listeTabItems: Array<PageHeaderItem> = FraisFacturesComponent.listeOnglets;

    /** Référence au namespace NatureVoyage pour l'utiliser dans le template */
    NatureVoyage = NatureVoyage;

    /**
     * Constructeur
     * 
     * @param typesChargesService Service de gestion des types de charges
     * @param toastrService Service pour afficher les messages de succès, d'erreurs, ...
     * @param translateService Service de traduction
     * @param confirmService Service de confirmation pour la suppression
     * @param activatedRoute Route d'accès à cette page
     * @param router Router de l'application pour naviguer entre les pages
     * @param dialog Service de dialogue pour la sélection d'icônes
     */
    constructor(private typesChargesService: TypesChargesService,
                private toastrService: ToastrService,
                private translateService: TranslateService,
                private confirmService: ConfirmService,
                private activatedRoute: ActivatedRoute,
                private fraisFacturesService: FraisFacturesService,
                private router: Router,
                private dialog: MatDialog) {
    }

    /**
     * Initialisation du composant
     */
    ngOnInit() {
        //Initialisation des onglets
        this.listeTabItems = [{
            code: Onglets.TYPES_CHARGES,
            libelle: 'admin.frais.typesCharges.infos.generalites',
        }];

        //Récupération de l'identifiant du type de charge
        let idTypeCharge = this.activatedRoute.snapshot.params.idTypeCharge;

        //Indique au composant géographie qu'on affiche le détail d'une adresse
        this.fraisFacturesService.isShowDetail = true;

        //Indique si on est en mode création
        this.isCreation = idTypeCharge === '0';

        //Définition de la liste des actions possibles
        let actions: Array<FloatingButtonAction> = [
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'global.actions.enregistrer',
                doAction: () => this.save(),
                isDisabled: () => this.form && this.form.invalid
            }
        ];

        //Récupération du type de charge
        if (this.isCreation) {
            //Création d'un nouveau type de charge
            this.typeCharge = new TypeCharge({
                idTypeCharge: -1,
                code: "",
                libelle: "",
                idTypeFile: "",
                codeFamille: null,
            });
        } else {
            //On ajoute l'action de suppression d'un type de charge quand on est en modification, pour ne pas l'avoir en création
            actions.push({
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                doAction: () => this.delete()
            });

            //Récupération du type de charge existant
            this.typesChargesService.getTypeChargeById(idTypeCharge).subscribe((result: any) => {
                // Vérifier si le résultat est valide
                if (result) {
                    this.typeCharge = result.data.typeCharge;
                } else {
                    //Gestion de l'erreur
                    TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
                }
            });
        }

        //Mise à jour des actions
        this.listeActions.next(actions);
    }

    /**
     * Sauvegarde des changements apportés au type de charge
     */
    save() {
        if (this.form.valid) {
            //Sauvegarde des changements en base
            this.typesChargesService.saveTypeCharge(this.isCreation, this.typeCharge).subscribe((result: Result<{typeCharge: TypeCharge}>) => {
                //Si la réponse contient un code d'erreur, affichage d'un message d'erreur
                if (result.codeErreur !== TypeCodeErreur.NO_ERROR) {
                    //Affichage d'un message d'erreur quand l'enregistrement du type de charge en base n'a pas fonctionné correctement
                    TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
                } else {
                    //Affichage d'un message de succès si l'enregistrement du type de charge en base s'est passé correctement
                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                    //Si on est en création alors on redirige vers la liste après avoir créé le type de charge, sinon on reste sur la page du type de charge
                    if (this.isCreation) {
                        //Affectation du type de charge nouvellement créé
                        this.typeCharge = result.data.typeCharge;
                        //Modification de l'URL vers la page details du type de charge
                        this.router.navigate(['/Admin/Frais/Factures/TypesCharges', result.data.typeCharge.idTypeCharge], { replaceUrl: true });
                        //Mise à jour de l'indication d'ajout
                        this.isCreation = false;
                        //Mise à jour de la liste des actions
                        this.listeActions.next([
                            {
                                type: TypeAction.PRIMARY,
                                icone: 'nio icon-sauvegarde',
                                libelle: 'global.actions.enregistrer',
                                doAction: () => this.save(),
                                isDisabled: () => this.form && this.form.invalid
                            },
                            {
                                type: TypeAction.SECONDARY,
                                icone: 'nio icon-suppression',
                                libelle: 'global.actions.supprimer',
                                doAction: () => this.delete()
                            }
                        ]);
                    }
                }
            });
        } else {
            //Affichage d'un message d'erreur indiquant que le formulaire n'est pas valide
            this.toastrService.error(this.translateService.instant('global.errors.formInvalid'));
        }
    }

    /**
     * Méthode appelée lors de la suppression du type de charge sélectionné
     */
    delete() {
        //Affichage de la confirmation de suppression du type de charge
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe({
                next: () => {
                    //Suppression du type de charge en base
                    this.typesChargesService.deleteTypeCharge(this.typeCharge.idTypeCharge).subscribe(data => {
                        //Vérification que data existe
                        if (data) {
                            //Si le code d'erreur du retour est différent de NO_ERROR alors c'est que le type de charge ne s'est pas supprimé correctement dans la base de données
                            if (data.codeErreur !== TypeCodeErreur.NO_ERROR) {
                                //Affichage d'un message d'erreur quand la suppression du type de charge en base n'a pas fonctionné correctement
                                this.toastrService.error(this.translateService.instant('global.errors.suppression'));
                            } else {
                                //Affichage d'un message de succès si la suppression du type de charge en base s'est passée correctement
                                this.toastrService.success(this.translateService.instant('global.success.suppression'));
                                //On retourne sur la liste des types de charges
                                this.router.navigate(['Admin/Frais/Factures/TypesCharges']);
                            }
                        } else {
                            //Affichage d'un message d'erreur si aucune donnée n'est retournée
                            this.toastrService.error(this.translateService.instant('global.errors.suppression'));
                        }
                    });
                }
            });
    }

    /**
     * Ouvre la boîte de dialogue de sélection d'icônes
     */
    openIconeDialog() {
        //Sauvegarde du code de famille actuel avant ouverture du dialogue
        const codeFamilleActuel = this.typeCharge.codeFamille;
        
        const dialogRef = this.dialog.open(FacturesTypesChargesInfosIconesComponent, {
            width: '500px',
            data: {codeFamille: codeFamilleActuel}
        });

        dialogRef.afterClosed().subscribe(result => {
            //Si result est true (fermeture avec le X) ou undefined (fermeture sans sélection), on conserve le code de famille actuel
            if (result === true || result === undefined) {
                //Pas besoin de réassigner car la valeur n'a pas changé
            } else if (result) {
                //Si result est une valeur valide (code de nature voyage), on l'applique
                this.typeCharge.codeFamille = result;
            } else {
                //Si result est null, false, ou une chaîne vide, on met à null pour afficher la loupe
                this.typeCharge.codeFamille = null;
            }
        });
    }

    /**
     * Destruction
     */
    ngOnDestroy() {
        //Indique au composant géographie qu'on n'affiche plus le détail d'un pays
        this.fraisFacturesService.isShowDetail = false;
    }

    /**
     * Méthode appelée lors du clic sur le bouton de retour en arrière pour retourner vers la liste des types de charges
     */
    onGoBackTypesCharges(): void {
        this.router.navigate(['Admin/Frais/Factures/TypesCharges']);
    }
}